/**
 * 학생 통계 API 서비스
 */
import { API_BASE_URL } from '../../../config/api';
import type {
  StudyTimeData,
  WeeklyStats,
  StudyGoal,
  CounselingRecordSummary,
  MovementSummary,
} from '../models/StudentStatistics';

export class StudentStatisticsService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * 인증 헤더 생성
   */
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * 오늘의 학습 통계 조회
   */
  async getTodayStudyTime(): Promise<StudyTimeData> {
    const response = await fetch(`${this.baseUrl}/my/study-time/today`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`학습 통계 조회 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 특정 날짜의 학습 통계 조회
   */
  async getStudyTimeByDate(date: string): Promise<StudyTimeData> {
    const response = await fetch(`${this.baseUrl}/my/study-time/date/${date}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`학습 통계 조회 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 주간 학습 통계 조회
   */
  async getWeeklyStats(): Promise<WeeklyStats> {
    const response = await fetch(`${this.baseUrl}/my/study-time/weekly`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`주간 통계 조회 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 내 학습 목표 조회 (활성 목표)
   */
  async getMyActiveGoals(studentId: string): Promise<StudyGoal[]> {
    const response = await fetch(
      `${this.baseUrl}/study-goals/student/${studentId}/active`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`학습 목표 조회 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 학습 목표 생성
   */
  async createGoal(goalData: {
    goal_setter: 'student';
    goal_period: 'weekly' | 'monthly';
    start_date: string;
    end_date: string;
    daily_study_hours?: number;
    weekly_attendance_days?: number;
    target_subjects?: Record<string, number>;
    notes?: string;
  }): Promise<StudyGoal> {
    const response = await fetch(`${this.baseUrl}/study-goals/`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(goalData),
    });

    if (!response.ok) {
      throw new Error(`목표 생성 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 최근 상담 기록 조회
   */
  async getRecentCounselingRecords(studentId: string, limit: number = 3): Promise<CounselingRecordSummary[]> {
    const response = await fetch(
      `${this.baseUrl}/counseling-records/student/${studentId}/summary`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`상담 기록 조회 실패: ${response.status}`);
    }

    const data = await response.json();
    // 최근 N개만 반환
    return Array.isArray(data) ? data.slice(0, limit) : [];
  }

  /**
   * 오늘의 동선 분석 조회
   */
  async getTodayMovements(): Promise<MovementSummary> {
    const response = await fetch(`${this.baseUrl}/my/movements/today`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`동선 분석 조회 실패: ${response.status}`);
    }

    return response.json();
  }
}

// 싱글톤 인스턴스
export const studentStatisticsService = new StudentStatisticsService();
